import { useEffect, useRef } from "react";

const VERT = `attribute vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }`;

const FRAG = `precision highp float;
uniform float u_time;
uniform vec2 u_resolution;

// Simplex-style noise
vec3 mod289(vec3 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                     -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
    + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy),
    dot(x12.zw,x12.zw)), 0.0);
  m = m*m; m = m*m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

float fbm(vec2 p) {
  float f = 0.0;
  float w = 0.5;
  for (int i = 0; i < 5; i++) {
    f += w * snoise(p);
    p *= 2.0;
    w *= 0.5;
  }
  return f;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  float aspect = u_resolution.x / u_resolution.y;
  vec2 p = (uv - 0.5) * vec2(aspect, 1.0);
  float t = u_time * 0.08;

  // Domain warping — multiple layers
  vec2 q = vec2(
    fbm(p + vec2(0.0, 0.0) + t * 0.4),
    fbm(p + vec2(5.2, 1.3) + t * 0.3)
  );

  vec2 r = vec2(
    fbm(p + 4.0 * q + vec2(1.7, 9.2) + t * 0.15),
    fbm(p + 4.0 * q + vec2(8.3, 2.8) + t * 0.2)
  );

  float f = fbm(p + 3.5 * r);

  // Color palette — dark teal/cyan nebula
  vec3 col = vec3(0.0);

  // Deep background: near-black with subtle blue
  vec3 bg = vec3(0.04, 0.04, 0.06);

  // Teal glow layer
  vec3 teal = vec3(0.0, 0.83, 0.67);  // #00d4aa

  // Deep blue accent
  vec3 deepBlue = vec3(0.05, 0.15, 0.35);

  // Warm highlight
  vec3 warm = vec3(0.0, 0.55, 0.65);

  col = mix(bg, deepBlue, clamp(f * f * 2.0, 0.0, 1.0));
  col = mix(col, teal * 0.4, clamp(length(q) * 0.6, 0.0, 1.0));
  col = mix(col, warm, clamp(pow(length(r), 2.0) * 0.5, 0.0, 1.0));

  // Bright teal highlights in the folds
  float highlight = clamp(f * 2.5 - 0.3, 0.0, 1.0);
  col += teal * highlight * 0.15;

  // Subtle vignette
  float vig = 1.0 - 0.4 * length(uv - 0.5);
  col *= vig;

  // Fade to background at bottom for content blend
  float bottomFade = smoothstep(0.0, 0.25, uv.y);
  col = mix(vec3(0.067, 0.067, 0.063), col, bottomFade);

  gl_FragColor = vec4(col, 1.0);
}`;

export default function HeroShader({ height = "100vh", style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { alpha: false, antialias: false });
    if (!gl) return;

    // Compile shaders
    function compile(type, src) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(s));
        return null;
      }
      return s;
    }

    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.useProgram(prog);

    // Full-screen quad
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "a_position");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const uTime = gl.getUniformLocation(prog, "u_time");
    const uRes = gl.getUniformLocation(prog, "u_resolution");

    let isIntersecting = false;
    let isDocumentVisible = !document.hidden;
    let prefersReducedMotion = false;
    let needsStaticFrame = false;
    let animationTime = 0;
    let lastFrameTime = null;
    let cleanedUp = false;

    function draw() {
      gl.uniform1f(uTime, animationTime);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
      if (prefersReducedMotion) {
        needsStaticFrame = true;
        updateAnimation();
      }
    }

    resize();
    window.addEventListener("resize", resize);

    function render(now) {
      if (lastFrameTime !== null) {
        animationTime += (now - lastFrameTime) / 1000;
      }
      lastFrameTime = now;
      draw();
      rafRef.current = requestAnimationFrame(render);
    }

    function pause() {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastFrameTime = null;
    }

    function updateAnimation() {
      if (cleanedUp || prefersReducedMotion || !isIntersecting || !isDocumentVisible) {
        pause();
        if (!cleanedUp && prefersReducedMotion && isIntersecting && isDocumentVisible && needsStaticFrame) {
          draw();
          needsStaticFrame = false;
        }
        return;
      }
      if (rafRef.current === null) {
        lastFrameTime = performance.now();
        draw();
        rafRef.current = requestAnimationFrame(render);
      }
    }

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    prefersReducedMotion = motionQuery.matches;
    needsStaticFrame = prefersReducedMotion;

    const observer = new IntersectionObserver(([entry]) => {
      isIntersecting = entry.isIntersecting;
      updateAnimation();
    });
    observer.observe(canvas);

    function handleVisibilityChange() {
      isDocumentVisible = !document.hidden;
      updateAnimation();
    }

    function handleMotionChange(event) {
      prefersReducedMotion = event.matches;
      if (prefersReducedMotion) {
        needsStaticFrame = true;
      }
      updateAnimation();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    motionQuery.addEventListener("change", handleMotionChange);

    return () => {
      cleanedUp = true;
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      motionQuery.removeEventListener("change", handleMotionChange);
      observer.disconnect();
      pause();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        ...style,
      }}
    />
  );
}
